import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import downloadIcon from '../assets/img/descarga.png';
import docxIcon from '../assets/img/docx.png';
import pdfIcon from '../assets/img/pdf.png';
import txtIcon from '../assets/img/txt.png';
import videoIcon from '../assets/img/video-descarga.png';
import musicaIcon from '../assets/img/musica.png';
import shareIcon from '../assets/img/compartir.png';
import likeIconR from '../assets/img/recomendar-white.png';
import likeIcon from '../assets/img/recomendar-blue.png';
import MediaPlayer from './MediaPlayer';
import ProductDetailView from './ProductDetailView';
import { useNavigate, useParams } from 'react-router-dom';
import userIcon from '../assets/img/user.png';

// ─── CONSTANTE: cuántas imágenes cargar en paralelo ───────────────────────────

const ContentView = ({ themeIds = [], token, username, userId, filterContents, searchRecommends, updateNotificationCount, theme, isLoggedIn, isSidebarOpen, onViewDetailChange, role, storeRif }) => {
  const [contents, setContents] = useState([]);
  const [error, setError] = useState(null);
  const [images, setImages] = useState({});
  const [filteredContents, setFilteredContents] = useState([]);
  const [recommends, setRecommends] = useState({});
  const [likeStates, setLikeStates] = useState({});
  const [loading, setLoading] = useState(true);   // ← empieza en TRUE siempre
  const [selectedContent, setSelectedContent] = useState(null);
  const [shareMenuId, setShareMenuId] = useState(null);
  const [shareLinks, setShareLinks] = useState({});

  const isDarkMode = theme === 'dark';

  const navigate = useNavigate();
  const { slug } = useParams();
  const basePath = slug ? `/creador/${slug}` : '/dashboard';

  const getRifIcon = (rif) => {
    if (!rif) return null;
    const prefix = rif.trim().charAt(0).toUpperCase();
    const icons = {
      J: '🏢', // Empresa / persona jurídica
      G: '🏛️', // Gobierno
      V: '👤', // Persona natural
      E: '🌐', // Extranjero
      P: '🛂', // Pasaporte
    };
    return icons[prefix] || '📄';
  };

  // Cierra el menú de compartir al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = () => setShareMenuId(null);
    if (shareMenuId) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [shareMenuId]);

  // ── HELPER para enviar notificación ──
  // El backend crea la notificación para el DUEÑO del contenido (no para quien actúa).
  // updateNotificationCount NO se llama aquí porque actualizaría el contador
  // del actor (quien recomienda/descarga/comparte), no del receptor.
  // El receptor (dueño) se entera via polling en el componente padre.
  const sendNotification = async (contentId, type) => {
    if (!isLoggedIn) return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/user/notifications`, {
        contentId, username, type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Si el actor ES el dueño del contenido (ej: se descarga su propio contenido),
      // no tiene sentido notificarse a sí mismo — el backend ya lo filtra con recipients.delete()
      console.log(`[Notif] tipo="${type}" enviada →`, res.data);
    } catch (err) {
      console.error('[Notif] Error enviando notificación:', err?.response?.data || err.message);
    }
  };

  // ── RECOMENDAR ──
  const updateLikesAndRecommends = async (contentId, action) => {
    const wasLiked = likeStates[contentId] || false;
    const currentCount = recommends[contentId] || 0;
    const nowLiked = !wasLiked;

    // ✅ Actualiza UI AL INSTANTE antes de cualquier llamada
    setLikeStates(prev => ({ ...prev, [contentId]: nowLiked }));
    setRecommends(prev => ({ ...prev, [contentId]: nowLiked ? currentCount + 1 : Math.max(0, currentCount - 1) }));

    // Lanza notificación sin bloquear (fire and forget)
    if (nowLiked) {
      sendNotification(contentId, 'recommend');
    }

    try {
      // API en segundo plano — solo confirma el valor real
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/update-like-recommend`,
        { contentId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Sincroniza con el valor real del servidor silenciosamente
      setRecommends(prev => ({ ...prev, [contentId]: response.data.recommends }));
      setLikeStates(prev => ({ ...prev, [contentId]: response.data.userRecommended }));
    } catch (error) {
      // Revertir si falla
      console.error('Error updating likes/recommends:', error);
      setLikeStates(prev => ({ ...prev, [contentId]: wasLiked }));
      setRecommends(prev => ({ ...prev, [contentId]: currentCount }));
    }
  };

  const getLikeRecommendCount = (contentId) => recommends[contentId] || 0;

  const canDownload = (content) => {
    if (content.type === 'video') return false;
    if (!content.isDigitalProduct) return true;
    const isCreator = String(content.userId?._id || content.userId) === String(userId);
    if (isCreator) return true;
    const isRevoked = Array.isArray(content.revokedUsers) &&
      content.revokedUsers.some(u => String(u._id || u) === String(userId));
    if (isRevoked) return false;
    const isAssigned = Array.isArray(content.assignedTo) &&
      content.assignedTo.some(a => String(a._id || a) === String(userId));
    if (isAssigned) return true;
    return Array.isArray(content.downloadAllowedUsers) &&
      content.downloadAllowedUsers.some(u => String(u._id || u) === String(userId));
  };

  // ── FUNCIÓN HELPER: aplica filtros locales ──────────────────────────────────
  const applyLocalFilters = useCallback((rawContents) => {
  let filtered = (!themeIds || themeIds.length === 0)
    ? [...rawContents]
    : rawContents.filter(c => {
        // Busca en themeIds (nuevo) y themeId (viejo)
        const contentThemeIds = [
          ...(Array.isArray(c.themeIds) ? c.themeIds.map(t => String(t._id || t)) : []),
          ...(c.themeId ? [String(c.themeId._id || c.themeId)] : []),
        ];
        return themeIds.some(selected => contentThemeIds.includes(String(selected)));
      });

    filtered = filtered.filter(content => {
      if (content.visibility === 'privado') {
        const isOwner = String(content.userId?._id || content.userId) === String(userId);
        const isAssigned = Array.isArray(content.assignedTo) &&
          content.assignedTo.some(a => String(a._id) === String(userId));
        return isOwner || isAssigned;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      // ✅ Usa el state local que se actualiza al instante
      const rA = recommends[a._id] !== undefined ? recommends[a._id] : (a.recommends?.length || 0);
      const rB = recommends[b._id] !== undefined ? recommends[b._id] : (b.recommends?.length || 0);
      if (rB !== rA) return rB - rA;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [themeIds, userId, recommends]);

  // ── CARGA DE IMÁGENES EN PARALELO (por lotes) ───────────────────────────────
  const loadImagesParallel = useCallback(async (contentsToLoad) => {
  const needsImage = contentsToLoad.filter(c => c.type === 'imagen' || c.isDigitalProduct);
  if (!needsImage.length) return;

  const imageMap = {};
  const needsFetch = [];

  needsImage.forEach(c => {
    // Tipo imagen: usar file directamente
    if (c.type === 'imagen') {
      const fileStr = Array.isArray(c.file) ? c.file[0] : c.file;
      if (fileStr && fileStr.startsWith('http')) {
        imageMap[c._id] = fileStr;
        return;
      }
    }
    // Producto digital: usar image si existe
    if (c.isDigitalProduct && c.image && c.image.startsWith('http')) {
      imageMap[c._id] = c.image;
      return;
    }
    // Sin imagen directa → necesita fetch al backend
    needsFetch.push(c);
  });

  setImages(prev => ({ ...prev, ...imageMap }));

  // Solo fetchar los que no tienen imagen directa
  await Promise.allSettled(
    needsFetch.map(async (content) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/img-content/${content._id}`
        );
        if (response.data.image) {
          setImages(prev => ({ ...prev, [content._id]: response.data.image }));
        }
      } catch {
        // sin imagen, dejar vacío
      }
    })
  );
}, []);


  // ── PASO 1: useLayoutEffect → fuerza loading=true ANTES del primer paint ─────
  useLayoutEffect(() => {
    setLoading(true);
  }, [filterContents, themeIds, token]);

  // ── PASO 2: useEffect → procesa datos DESPUÉS de que el skeleton ya se pintó ─
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setError(null);

      // 2. Caso: filtro vacío (sin resultados)
      if (Array.isArray(filterContents) && filterContents.length === 0) {
        setContents([]);
        setFilteredContents([]);
        setLoading(false);
        return;
      }

      try {
        let baseContents = [];

        if (filterContents) {
          // Filtro externo ya tiene los datos en memoria.
          // Diferimos con setTimeout para que React haga flush del skeleton primero.
          await new Promise(resolve => setTimeout(resolve, 0));
          if (cancelled) return;
          baseContents = filterContents;
        } else if (!themeIds || themeIds.length === 0) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/user/contentsTotal`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          baseContents = response.data;
        }

        if (cancelled) return;

        // Aplicar filtros locales
        const filtered = applyLocalFilters(baseContents);

        // Mostrar contenidos → quita el skeleton
        setContents(baseContents);
        setFilteredContents(filtered);
        setLoading(false);

        // Cargar imágenes en segundo plano
        // (los recommends los manda DashboardHome via searchRecommends)
        loadImagesParallel(filtered);

      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching contents:', err);
          setError('Error al obtener los contenidos');
          setLoading(false);
        }
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [filterContents, themeIds, token]);

  // ── Cuando cambia themeId sin nuevo fetch (ya tenemos contents), re-filtra ──
  // Solo aplica cuando ya hay datos cargados y NO hay filterContents externo
  useEffect(() => {
  if (!filterContents && contents.length > 0) {
    setFilteredContents(applyLocalFilters(contents));
  }
}, [themeIds, contents, userId, applyLocalFilters, filterContents]);

  // ── searchRecommends externo: sincroniza conteos Y estado azul del ícono ──
  useEffect(() => {
    if (!searchRecommends) return;
    // Nuevo formato: { counts: {id: n}, likeStates: {id: bool} }
    if (searchRecommends.counts) {
      setRecommends(searchRecommends.counts);
      setLikeStates(searchRecommends.likeStates || {});
    } else {
      // Compatibilidad con formato antiguo (solo conteos)
      setRecommends(searchRecommends);
    }
  }, [searchRecommends]);

  // ── Notificar vista de detalle al padre ──
  useEffect(() => {
    if (onViewDetailChange) onViewDetailChange(selectedContent !== null);
  }, [selectedContent, onViewDetailChange]);

  /* ── DESCARGAR ── */
  const downloadFile = async (contentId) => {
    try {
      const content = filteredContents.find(c => c._id === contentId);
      const fileStr = Array.isArray(content?.file) ? content.file[0] : content?.file;
      const extension = fileStr ? fileStr.substring(fileStr.lastIndexOf('.')) : '';
      const cleanFileName = content?.title
        ? `${content.title.replace(/[/\\?%*:|"<>]/g, '-')}${extension}`
        : fileStr
          ? fileStr.replace(/^\d+_/, '').replace(/^\d+\.\s?/, '')
          : `archivo_${contentId}${extension}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/download-content/${contentId}`,
        { responseType: 'blob', timeout: 50000, headers: { 'Authorization': `Bearer ${token}` } }
      );

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = cleanFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (sendNotification) await sendNotification(contentId, 'download');
    } catch (error) {
      console.error('Error downloading file:', error);
      let msg = 'Ha ocurrido un error al descargar el archivo.';
      if (error.response?.status === 404) msg = 'El archivo no está disponible en el servidor.';
      else if (error.response) msg += ` Código de error: ${error.response.status}`;
      alert(msg);
    }
  };

  /* ── COMPARTIR ── */
  const handleShare = async (e, content) => {
    e.stopPropagation();
    const storeParam = slug ? `?store=${slug}` : '';
    const ogUrl = `${process.env.REACT_APP_API_URL}/api/user/og/content/${content._id}${storeParam}`;
    const shareText = `¡Mira este contenido: ${content.title}!`;
    const whatsapp = `http://wa.me/?text=${encodeURIComponent(shareText + ' ' + ogUrl)}`;
    const telegram = `http://t.me/share/url?url=${encodeURIComponent(ogUrl)}&text=${encodeURIComponent(shareText)}`;
    const twitter  = `http://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(ogUrl)}`;
    const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`;
    setShareLinks({ whatsapp, telegram, twitter, facebook, url: ogUrl });
    setShareMenuId(prev => prev === content._id ? null : content._id);
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────

  // Skeleton: se muestra SOLO cuando loading es true
  if (loading) {
    return (
      <Container>
        <ContentsContainer>
          <ContentsList isLoggedIn={isLoggedIn} isSidebarOpen={isSidebarOpen}>
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonContentItem key={i} active={isDarkMode} />
            ))}
          </ContentsList>
        </ContentsContainer>
      </Container>
    );
  }

  if (error && contents.length === 0) return <ErrorText>{error}</ErrorText>;

  if (contents.length === 0) {
    return (
      <Loader>
        <p>No hay contenidos disponibles</p>
      </Loader>
    );
  }

  if (selectedContent) {
    return (
      <ProductDetailView
        content={selectedContent}
        theme={theme}
        token={token}
        images={images}
        userId={userId}
        onBack={() => setSelectedContent(null)}
      />
    );
  }

  if (filteredContents.length === 0) {
    return (
      <Loader>
        <div style={{
          width: '70px', height: '70px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)', border: '2px solid rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px'
        }}>🔍</div>
        <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-main)' }}>
          No se encontró contenido
        </p>
        <p style={{ margin: 0, fontSize: '14px', color: '#888', textAlign: 'center', maxWidth: '280px' }}>
          Intenta con otra temática, palabra clave o rango de fechas
        </p>
      </Loader>
    );
  }

  return (
    <Container>
      <ContentsContainer>
        <ContentsList isLoggedIn={isLoggedIn} isSidebarOpen={isSidebarOpen}>
          {filteredContents.map((content) => (
            <ContentItem key={content._id} active={isDarkMode}>

               {/* ── Stock badge en el borde imagen/info ── */}
                {content.stock != null && content.stock !== -1 && (
                  <StockBadgeOverlay stock={content.stock}>
                    <StockDot stock={content.stock} />
                    {content.stock === 0 ? '🏆 100% vendido · Solicitar' : `${content.stock} Disponible`}
                  </StockBadgeOverlay>
                )}

              {/* ── MEDIA ── */}
              {content.type === 'imagen' ? (
                <ContainerT
                  active={isDarkMode}
                  onClick={() => {
                    const fileStr = Array.isArray(content.file) ? content.file[0] : content.file;
                    const fileLower = fileStr?.toLowerCase() || '';
                    const isVideo = fileLower.endsWith('.mp4') || fileLower.endsWith('.mov') || fileLower.endsWith('.webm') || content.type === 'video';
                    if (!isVideo) setSelectedContent(content);
                  }}
                >
                  {(() => {
                    const fileStr = Array.isArray(content.file) ? content.file[0] : content.file;
                    const fileLower = fileStr?.toLowerCase() || '';
                    const isVideo = fileLower.endsWith('.mp4') || fileLower.endsWith('.mov') || fileLower.endsWith('.webm') || content.type === 'video';
                    const isAudio = fileLower.endsWith('.mp3') || fileLower.endsWith('.wav') || fileLower.endsWith('.m4a');

                    if (isVideo || isAudio) {
                      return (
                        <MediaPlayer
                          mediaUrl={fileStr} mediaType={isVideo ? 'video' : 'audio'}
                          isLocalFile={true} token={token} id={content._id}
                        />
                      );
                    }

                    if (images[content._id]) {
                      return (
                        <ContentImage
                          src={images[content._id]} alt={content.title}
                          onError={(e) => { e.target.src = require('../assets/img/simbolo-de-imagen-n.png'); }}
                        />
                      );
                    }
                    return null;
                  })()}
                </ContainerT>
              ) : content.type === 'video' ? (
                <ContainerT active={isDarkMode} onClick={() => setSelectedContent(content)}>
                  <ContentVideo
                    src={`https://www.youtube.com/embed/${new URL(content.videoUrl).searchParams.get('v')}`}
                    title={content.title}
                  />
                </ContainerT>
              ) : (
                <ContainerT
                  active={isDarkMode}
                  onClick={() => {
                    const imgField = content.image || '';
                    const isVideoPortada = imgField.toLowerCase().endsWith('.mp4') || imgField.toLowerCase().endsWith('.mov');
                    if (!isVideoPortada) setSelectedContent(content);
                  }}
                >
                  {content.isDigitalProduct ? (
                    (() => {
                      const imgField = content.image || '';
                      const imgLower = imgField.toLowerCase();
                      const isVideoPortada = imgLower.endsWith('.mp4') || imgLower.endsWith('.mov');
                      const isAudioPortada = imgLower.endsWith('.mp3');

                      if (isVideoPortada || isAudioPortada) {
                        return (
                          <MediaPlayer
                            mediaUrl={imgField} mediaType={isVideoPortada ? 'video' : 'audio'}
                            isLocalFile={true} token={token} id={content._id} isCover={true}
                          />
                        );
                      }

                      if (images[content._id]) {
                        return (
                          <ContentImage
                            src={images[content._id]} alt={content.title}
                            onError={(e) => { e.target.src = require('../assets/img/simbolo-de-imagen-n.png'); }}
                          />
                        );
                      }
                      return null;
                    })()
                  ) : content.file && (
                    <>
                      {(() => {
                        const fileStr = Array.isArray(content.file) ? content.file[0] : content.file;
                        if (!fileStr) return null;
                        const fileLower = fileStr.toLowerCase();

                        return (fileLower.endsWith('.mp3') || fileLower.endsWith('.mp4')) ? (
                          <MediaPlayer
                            mediaUrl={fileStr} mediaType={fileLower.endsWith('.mp4') ? 'video' : 'audio'}
                            isLocalFile={true} token={token} id={content._id}
                          />
                        ) : (
                          <ContentArchivo>
                            <img
                              src={
                                fileLower.endsWith('.docx') ? docxIcon :
                                fileLower.endsWith('.pdf')  ? pdfIcon  :
                                fileLower.endsWith('.txt')  ? txtIcon  :
                                fileLower.endsWith('.mp3')  ? musicaIcon :
                                fileLower.endsWith('.mp4')  ? videoIcon :
                                downloadIcon
                              }
                              alt="Descargar archivo"
                              onClick={() => canDownload(content) && downloadFile(content._id)}
                              style={{ marginTop: '100px', cursor: canDownload(content) ? 'pointer' : 'not-allowed', width: '50px', height: 'auto' }}
                            />
                          </ContentArchivo>
                        );
                      })()}
                    </>
                  )}
                </ContainerT>
              )}

              {/* ── INFO ── */}
              <ContentInfo active={isDarkMode}>
                <DetailButtonContainer isLoggedIn={isLoggedIn}>
                      <DetailButton active={isDarkMode} onClick={() => setSelectedContent(content)}>
                        <InfoIcon>ⓘ</InfoIcon>
                        <TitleGroup title={content.title}>{content.title}</TitleGroup>
                      </DetailButton>
                      {content.price && content.price !== 0 && content.price !== '0' ? (
                        <PriceTag active={isDarkMode}>
                          <span className="currency">$</span>
                          {Number(content.price).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </PriceTag>
                      ) : null}
                    </DetailButtonContainer>

                {isLoggedIn && (
                  <>
                    <RecommendCounter
                      isSidebarOpen={isSidebarOpen}
                      style={{
                        textShadow: likeStates[content._id]
                          ? '0 0 5px #0a66c2, 0 0 5px #0a66c2'
                          : '-1px -1px 0 #3d3d3d, 1px -1px 0 #3d3d3d, -1px 1px 0 #3d3d3d, 1px 1px 0 #3d3d3d',
                        color: 'white'
                      }}
                    >
                      {recommends[content._id] !== undefined
                        ? getLikeRecommendCount(content._id)
                        : <SpinnerDot />
                      }
                    </RecommendCounter>

                    <InfoTop isSidebarOpen={isSidebarOpen}>
                      <LikeButton isSidebarOpen={isSidebarOpen} onClick={() => updateLikesAndRecommends(content._id, 'recommend')}>
                        <img
                          src={likeStates[content._id] ? likeIcon : likeIconR}
                          alt="Me gusta"
                          style={{ width: '25px', marginRight: '5px', filter: likeStates[content._id] ? 'drop-shadow(0 0 3px #0a66c2)' : 'none' }}
                        />
                        <span style={{
                          textShadow: likeStates[content._id]
                            ? '0 0 5px #0a66c2, 0 0 5px #0a66c2'
                            : '-1px -1px 0 #3d3d3d, 1px -1px 0 #3d3d3d, -1px 1px 0 #3d3d3d, 1px 1px 0 #3d3d3d',
                          color: 'white', transition: 'all 0.3s ease'
                        }}>
                          Recomendar
                        </span>
                      </LikeButton>

                      <DownloadButton
                        isSidebarOpen={isSidebarOpen}
                        onClick={() => canDownload(content) && downloadFile(content._id)}
                        disabled={!canDownload(content)}
                        style={{ opacity: !canDownload(content) ? 0.3 : 1, cursor: !canDownload(content) ? 'not-allowed' : 'pointer' }}
                      >
                        <img src={downloadIcon} alt="Descargar" style={{ width: '25px', marginRight: '5px' }} />
                        {content.isDigitalProduct && !canDownload(content) ? '🔒 Bloqueado' : 'Descargar'}
                      </DownloadButton>

                      <ShareWrapper>
                        <ShareButton isSidebarOpen={isSidebarOpen} onClick={(e) => handleShare(e, content)}>
                          <img src={shareIcon} alt="Compartir" style={{ width: '25px', marginRight: '5px' }} />
                          Compartir
                        </ShareButton>
                        {shareMenuId === content._id && (
                          <ShareMenu onClick={e => e.stopPropagation()}>
                            <ShareOption href={shareLinks.whatsapp} target="_blank" rel="noreferrer" color="#25D366" onClick={() => sendNotification(content._id, 'share')}>
                              💬 WhatsApp
                            </ShareOption>
                            <ShareOption href={shareLinks.telegram} target="_blank" rel="noreferrer" color="#0088cc" onClick={() => sendNotification(content._id, 'share')}>
                              ✈️ Telegram
                            </ShareOption>
                            <ShareOption as="button" color="#aaa" onClick={() => {
                              navigator.clipboard.writeText(shareLinks.url);
                              alert('¡Enlace copiado!');
                              setShareMenuId(null);
                              sendNotification(content._id, 'share');
                            }}>
                              🔗 Copiar enlace
                            </ShareOption>
                          </ShareMenu>
                        )}
                      </ShareWrapper>
                    </InfoTop>
                  </>
                )}

                <InfoBottom>
                <CreatorLink
                  active={isDarkMode}
                  onClick={() => {
                    if (!isLoggedIn) {
                      navigate('/login');
                      return;
                    }
                    const creatorId = content.userId?._id || content.userId;
                    if (creatorId) navigate(`${basePath}/users/${creatorId}`);
                  }}
                >
                  <CreatorTopRow>
                    {storeRif && getRifIcon(storeRif) ? (
                      <CreatorEmojiIcon>{getRifIcon(storeRif)}</CreatorEmojiIcon>
                    ) : (
                      <CreatorIconImg src={userIcon} alt="creador" active={isDarkMode} />
                    )}
                    {storeRif && (
                      <>
                        <CreatorLabel active={isDarkMode}>RIF:</CreatorLabel>
                        <span>{storeRif}</span>
                      </>
                    )}
                  </CreatorTopRow>

                  {storeRif && <RifDivider active={isDarkMode}>|</RifDivider>}

                  <CreatorBottomRow>
                    <CreatorLabel active={isDarkMode}>Creador:</CreatorLabel>
                    <span>{content.userId?.username || 'Desconocido'}</span>
                  </CreatorBottomRow>
                </CreatorLink>
                </InfoBottom>

                {!isLoggedIn && (
                  <ShareWrapperPublic>
                    <ShareButton isSidebarOpen={isSidebarOpen} onClick={(e) => handleShare(e, content)}>
                      <img src={shareIcon} alt="Compartir" style={{ width: '20px', marginRight: '5px' }} />
                      Compartir
                    </ShareButton>
                    {shareMenuId === content._id && (
                      <ShareMenu onClick={e => e.stopPropagation()}>
                        <ShareOption href={shareLinks.whatsapp} target="_blank" rel="noreferrer" color="#25D366">💬 WhatsApp</ShareOption>
                        <ShareOption href={shareLinks.telegram} target="_blank" rel="noreferrer" color="#0088cc">✈️ Telegram</ShareOption>
                        <ShareOption as="button" color="#aaa" onClick={() => { navigator.clipboard.writeText(shareLinks.url); alert('¡Enlace copiado!'); setShareMenuId(null); }}>
                          🔗 Copiar enlace
                        </ShareOption>
                      </ShareMenu>
                    )}
                  </ShareWrapperPublic>
                )}
              </ContentInfo>
            </ContentItem>
          ))}
        </ContentsList>
      </ContentsContainer>
    </Container>
  );
};

/* ═══════════════════════════════════════════
   STYLED COMPONENTS (sin cambios)
═══════════════════════════════════════════ */

const Container = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  display: block;
  @media (max-width: 768px) { padding: 0; }
`;

const ContentsList = styled.ul`
  display: grid;
  grid-template-columns: ${props =>
    (!props.isLoggedIn || !props.isSidebarOpen) ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'};
  justify-content: stretch;
  justify-items: stretch;
  gap: 15px;
  padding: 15px;
  list-style-type: none;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.4s ease-in-out;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 768px)  { grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 8px; }
`;

const ContentItem = styled.li`
  background-color: ${props => props.active ? '#555' : '#fff'};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-height: ${props => props.isLoggedIn ? '400px' : 'auto'};
  @media (max-width: 768px) { min-height: 280px; }
`;

const InfoTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: nowrap;
  position: relative;
  gap: ${props => props.isSidebarOpen ? '5px' : '4px'};
  @media (max-width: 768px) { gap: 0; justify-content: space-around; padding: 10px 0; }
`;

const LikeButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: white;
  font-size: ${props => props.isSidebarOpen ? '16px' : '14px'};
  white-space: nowrap;
  padding: 0;
  position: relative;
  @media (max-width: 768px) { flex-direction: column; }
  span { @media (max-width: 768px) { display: none; } }
  img {
    width: ${props => props.isSidebarOpen ? '25px' : '20px'} !important;
    margin-right: ${props => props.isSidebarOpen ? '5px' : '3px'};
    @media (max-width: 768px) { margin-right: 0; width: 26px !important; }
  }
`;

const ShareWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ShareButton = styled.button`
  background-color: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: white;
  text-shadow: -1px -1px 0 #3d3d3d, 1px -1px 0 #3d3d3d, -1px 1px 0 #3d3d3d, 1px 1px 0 #3d3d3d;
  font-size: ${props => props.isSidebarOpen ? '17px' : '14px'};
  white-space: nowrap;
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  img {
    width: ${props => props.isSidebarOpen ? '25px' : '20px'} !important;
    margin-right: ${props => props.isSidebarOpen ? '5px' : '3px'};
    @media (max-width: 768px) { margin-right: 0; width: 24px !important; }
  }
  &:hover, &:focus {
    border-color: #a855f7;
    box-shadow: 0 0 5px #a855f7, 0 0 5px #a855f7;
    text-shadow: 0 0 5px #a855f7, 0 0 5px #a855f7;
    img { filter: drop-shadow(0 0 3px #a855f7); }
  }
  @media (max-width: 768px) { flex-direction: column; font-size: 0; padding: 0; }
`;

const ShareMenu = styled.div`
  position: absolute;
  bottom: 36px;
  right: 0;
  background: #1e1e1e;
  border: 1px solid #a855f7;
  border-radius: 10px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 200;
  min-width: 160px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);

  @media (max-width: 768px) {
    /* Móvil: Lo pegamos a la derecha pero con margen para que no se corte */
    right: -7px; 
    bottom: 50px; /* Lo subimos para que no tape los iconos de acción */
    min-width: 140px;
    /* Eliminamos cualquier transform que hayamos puesto antes */
    transform: none;
`;

const ShareOption = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
  &:hover { background: ${p => p.color}22; color: ${p => p.color}; }
`;

const DownloadButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: white;
  text-shadow: -1px -1px 0 #3d3d3d, 1px -1px 0 #3d3d3d, -1px 1px 0 #3d3d3d, 1px 1px 0 #3d3d3d;
  font-size: ${props => props.isSidebarOpen ? '17px' : '14px'};
  white-space: nowrap;
  padding: 0;
  @media (max-width: 768px) { flex-direction: column; font-size: 0; }
  img {
    width: ${props => props.isSidebarOpen ? '25px' : '20px'} !important;
    margin-right: ${props => props.isSidebarOpen ? '5px' : '3px'};
    @media (max-width: 768px) { margin-right: 0; width: 24px !important; }
  }
`;

const RecommendCounter = styled.div`
  position: relative;
  right: ${props => props.isSidebarOpen ? '130px' : '110px'};
  font-size: ${props => props.isSidebarOpen ? '13px' : '10px'};
  font-weight: bold;
  color: white;
  text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;
  text-align: center;
  transition: all 0.3s ease;
  margin-bottom: -2px;
  @media (max-width: 768px) {
    left: 17%;
    transform: translateX(-50%);
    top: -5px;
    font-size: 12px;
    text-align: center;
  }
`;


const Loader = styled.div`
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  height: 60vh; width: 100%;
  font-size: 18px; font-weight: 600;
  color: var(--text-main, #333); gap: 20px;
  @media (max-width: 768px) { height: 80vh; }
`;


const ContentsContainer = styled.div`
  border-radius: 8px; width: 100%;
  background-color: var(--bg-app);
`;

const ContentImage = styled.img`
  width: 100%; height: 100%;
  object-fit: contain; border-radius: 5px; display: block;
`;

const ContainerT = styled.div`
  position: relative; width: 100%; height: 255px;
  transition: height 0.3s ease-in-out, background-image 0.3s ease;
  background-color: ${props => props.active ? '#555' : '#f0f0f0'};
  border: 1px solid ${props => props.active ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  cursor: pointer;
`;

const ContentArchivo = styled.div`
  width: 100%; height: 100%;
  object-fit: contain; border-radius: 5px; display: block;
`;

const ContentVideo = styled.iframe`
  width: 100%; height: 240px;
  object-fit: cover; border-radius: 5px; display: block;
`;

const ContentInfo = styled.div`
  padding: ${props => props.isLoggedIn ? '15px' : '8px 12px'};
  transition: padding 0.3s ease;
  background-color: ${props => props.active ? '#555' : '#f0f0f0'};
  border: 1px solid ${props => props.active ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  color: ${props => props.active ? 'white' : '#333'};
  flex-grow: 0;
  display: flex; flex-direction: column;
  justify-content: flex-start;
  h4 { margin: 0; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  @media (max-width: 768px) { padding: 8px; h4 { font-size: 14px; } }
`;

const InfoBottom = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
`;

const ErrorText = styled.p`
  color: red; margin-bottom: 20px;
`;

const DetailButton = styled.button`
  background: transparent;
  color: ${props => props.active ? '#fff' : '#000000'};
  border: 1px solid ${props => props.active ? 'rgba(255,255,255,0.4)' : '#000000'};
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 0 1 auto;
  max-width: 180px;
  min-width: 0;
  overflow: hidden;
  &:hover, &:active {
    background: ${props => props.active ? 'rgba(255,255,255,0.2)' : '#7a8289'};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
  @media (max-width: 768px) {
    padding: 4px 10px;
    max-width: 130px;
    font-size: 12px;
    &:active { transform: scale(0.96); background: ${props => props.active ? 'rgba(255,255,255,0.2)' : '#7a8289'}; color: white; border-color: transparent; }
    .ver-text { font-size: 10px; }
  }
`;

const DetailButtonContainer = styled.div`
  display: flex; justify-content: center; align-items: center;
  padding: ${props => props.isLoggedIn ? '10px 0' : '4px 0'};
  width: 100%; gap: 10px;
  @media (max-width: 768px) {
    margin-top: 5px;
    justify-content: ${props => props.hasPrice ? 'space-between' : 'center'};
  }
`;

const PriceTag = styled.div`
  position: relative;
  font-size: 14px;
  font-weight: 800;
  color: ${props => props.active ? '#ffffff' : '#1a1a1a'};
  background: ${props => props.active ? 'rgba(0,0,0,0.3)' : 'rgba(255, 255, 255, 0.9)'};
  border: 1.5px solid #ffcc00;
  border-left-width: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  box-shadow: 2px 2px 10px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  &:hover { transform: scale(1.05); }
  .currency { font-size: 12px; color: #ffcc00; font-weight: 900; }
  @media (max-width: 768px) { font-size: 14px; padding: 3px 8px; border-radius: 5px; }
`;

const ShareWrapperPublic = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #888;
  min-width: 20px;
  transition: transform 0.2s ease;
  @media (max-width: 768px) { font-size: 12px; }
`;

const TitleGroup = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  text-align: left;
  min-width: 0;
`;

const SkeletonContentItem = styled.div`
  background-color: ${props => props.active ? '#555' : '#e0e0e0'};
  border-radius: 8px;
  height: 255px;
  width: 100%;
  margin-bottom: 15px;
  position: relative;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: -150px;
    height: 100%;
    width: 150px;
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%);
    animation: loading 1.2s infinite;
  }
  @keyframes loading {
    0% { left: -150px; }
    100% { left: 100%; }
  }
`;

const SpinnerDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.5);
  animation: pulse 1s infinite ease-in-out;
  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50%       { opacity: 1;   transform: scale(1.2); }
  }
`;

const CreatorLink = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  width: fit-content;
  padding: 4px 10px 4px 6px;
  border-radius: 20px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  transition: all 0.2s ease;
  color: ${props => props.active ? '#c084fc' : '#9333ea'};
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: rgba(168, 85, 247, 0.15);
    border-color: rgba(168, 85, 247, 0.6);
    color: #d8b4fe;
    transform: translateX(2px);
    box-shadow: 0 0 8px rgba(168, 85, 247, 0.3);
  }
  &:active { transform: scale(0.97); }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: auto;
    padding: 6px 12px;
    gap: 2px;
  }
`;

const CreatorTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CreatorBottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const RifDivider = styled.span`
  color: ${props => props.active ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'};
  margin: 0 2px;

  @media (max-width: 768px) {
    content: '';
    display: block;
    width: 50%;
    height: 1px;
    background: currentColor;
    opacity: 0.4;
    margin: 3px 0;
    /* oculta el caracter "|" en mobile, se ve como línea */
    font-size: 0;
  }
`;

const CreatorIconImg = styled.img`
  width: 16px;
  height: 16px;
  filter: ${props => props.active ? 'invert(0)' : 'invert(1)'};
  opacity: 0.7;
`;

const CreatorLabel = styled.span`
  color: ${props => props.active ? '#f1f1f1' : '#282828'};
  font-weight: 400;
  font-size: 11px;
  @media (max-width: 768px) { display: none; }
`;


const StockDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.stock === 0 ? '#F59E0B' : '#22c55e'};
  box-shadow: 0 0 5px ${p => p.stock === 0 ? 'rgba(245,158,11,0.7)' : 'rgba(34,197,94,0.7)'};
`;

const StockBadgeOverlay = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 244px;  /* 255px (altura ContainerT) - mitad del badge (~11px) */
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 12px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  color: ${p => p.stock === 0 ? '#633806' : '#fff'};
  background: ${p => p.stock === 0 ? 'rgb(255 231 171 / 95%)' : 'rgba(22,163,74,0.92)'};
  border: 1.5px solid rgba(255,255,255,0.2);
  box-shadow: 0 2px 10px rgba(0,0,0,0.4);
  backdrop-filter: blur(4px);
  @media (max-width: 768px) { top: 244px; font-size: 9.5px; padding: 2px 9px; }
`;
const CreatorEmojiIcon = styled.span`
  font-size: 14px;
  line-height: 1;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(168, 85, 247, 0.18);
  border: 1px solid rgba(168, 85, 247, 0.5);
  filter: none;
  flex-shrink: 0;
`;


export default ContentView;